// Componente ÚNICO de baixa do DNACore: usado no DNA Matrículas (processId) e na Gestão de Títulos (titleId)
// Regra: nominal → desconto (%, R$ ou VALOR FINAL) → valor de liquidação → encargos opcionais → recebido → saldo 0
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Loader2, Pencil, RotateCcw, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SplitPaymentLines, lineBody } from './SplitPaymentLines';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const pct = (v) => String(Number(v) || 0).replace('.', ',');
const brDate = (s) => (String(s || '').match(/^\d{4}-\d{2}-\d{2}/)
  ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');
const METHODS = [['pix', 'PIX'], ['cash', 'Dinheiro'], ['card_debit', 'Cartão Débito'],
  ['card_credit', 'Cartão Crédito'], ['pix_card', 'PIX no Cartão']];

export default function PaymentSettlementModal({ open, onOpenChange, titleId, processId,
  title, onSettled }) {
  const [snap, setSnap] = useState(null);
  const [method, setMethod] = useState('pix');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [quote, setQuote] = useState(null);
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [dMode, setDMode] = useState('auto');
  const [dType, setDType] = useState('final');
  const [dValue, setDValue] = useState('');
  const [reason, setReason] = useState('');
  const [valor, setValor] = useState('');
  const [useFee, setUseFee] = useState(true);
  const [useInterest, setUseInterest] = useState(true);
  const [iMode, setIMode] = useState('auto');      // auto | manual (juros deste pagamento)
  const [iType, setIType] = useState('amount');    // amount | percent
  const [iValue, setIValue] = useState('');
  const [iReason, setIReason] = useState('');
  const [form, setForm] = useState({ received_amount: '', installments: 1, brand: '', last4: '', nsu: '', authorization_code: '', acquirer: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [extras, setExtras] = useState([]);           // formas adicionais do MESMO recebimento
  useEffect(() => { if (open) setExtras([]); }, [open, titleId]);
  const isCard = ['card_debit', 'card_credit', 'pix_card'].includes(method);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const erro = (e, fb) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : fb);
  };

  const auto = snap?.automatic_discount;
  const cfg = snap?.late_charge_config || { late_fee_percent: 2, daily_interest_percent: 0.0333, grace_days: 0 };
  const original = r2(snap?.original_amount);
  const recebido = r2(snap?.received);
  const nominalBalance = r2(snap?.nominal_balance ?? Math.max(0, original - recebido));
  const autoDisc = r2(auto?.amount);

  // desconto manual: %, R$ ou VALOR FINAL (valor final elimina percentual quebrado)
  const manualDisc = useMemo(() => {
    const v = Number(dValue || 0);
    if (dType === 'final') return Math.max(0, r2(nominalBalance - Math.min(Math.max(v, 0), nominalBalance)));
    if (dType === 'percent') return Math.min(r2(original * Math.min(v, 100) / 100), nominalBalance);
    return Math.min(Math.max(r2(v), 0), nominalBalance);
  }, [dType, dValue, original, nominalBalance]);

  const discount = dMode === 'manual' ? manualDisc : autoDisc;
  const amountDue = Math.max(0, r2(original - discount));
  const settlement = Math.max(0, r2(amountDue - recebido));       // valor de liquidação desta baixa

  // atraso e encargos (dias corridos entre vencimento e pagamento)
  const diasAtraso = useMemo(() => {
    const d = String(snap?.due_date || '').slice(0, 10);
    if (!d || !paidAt) return 0;
    const ms = new Date(`${paidAt}T00:00:00`) - new Date(`${d}T00:00:00`);
    return Math.max(0, Math.round(ms / 86400000));
  }, [snap?.due_date, paidAt]);
  const atrasado = diasAtraso > 0 && settlement > 0;
  const diasJuros = diasAtraso;
  const multaCalc = atrasado ? r2(settlement * Number(cfg.late_fee_percent || 0) / 100) : 0;
  const jurosCalc = atrasado ? r2(settlement * Number(cfg.daily_interest_percent || 0) * diasJuros / 100) : 0;
  const jurosManual = iType === 'percent'
    ? Math.min(r2(settlement * Math.min(Number(iValue || 0), 100) / 100), settlement)
    : Math.min(Math.max(r2(iValue), 0), settlement);
  const jurosBase = iMode === 'manual' ? jurosManual : jurosCalc;
  const multa = atrasado && useFee ? multaCalc : 0;
  const juros = useInterest && (atrasado || iMode === 'manual') ? jurosBase : 0;
  const totalReceber = r2(settlement + multa + juros);

  const split = !!titleId && extras.length > 0;
  const extrasSum = r2(extras.reduce((s, l) => s + r2(l.amount), 0));
  const line1Gross = split
    ? (valor === '' ? Math.max(0, r2(totalReceber - extrasSum)) : Math.max(0, r2(valor)))
    : (valor === '' ? totalReceber : Math.min(r2(valor), totalReceber));
  const gross = split ? r2(line1Gross + extrasSum) : line1Gross;
  const splitOver = split && gross - totalReceber > 0.004;
  const fullDiscount = settlement <= 0 && recebido <= 0 && totalReceber <= 0;
  const saldoDepois = Math.max(0, r2(settlement - Math.min(gross, settlement)));
  const parcial = gross > 0 && saldoDepois > 0.009;
  const reasonNeeded = dMode === 'manual' && Math.abs(discount - autoDisc) > 0.005 && !reason.trim();
  const iReasonNeeded = useInterest && iMode === 'manual' && !iReason.trim();
  const fee = fullDiscount ? 0 : r2(quote?.fee_amount);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const url = titleId ? `/financial/titulos/${titleId}/settlement-quote`
        : `/enrollment/processes/${processId}/payment-quote`;
      const { data } = await api.get(url);
      setSnap({
        ...data,
        received: data.received ?? 0,
        balance: data.balance ?? data.amount_due,
        description: data.description || title,
      });
      setValor('');
    } catch (e) {
      erro(e, 'Não foi possível carregar os valores do título.');
      setSnap({ original_amount: 0, amount_due: 0, received: 0, balance: 0,
        automatic_discount: { amount: 0, percent: 0, source: 'NONE', label: 'Sem desconto' } });
    }
  }, [open, titleId, processId, title]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/financial/accounts/receiving', { params: { payment_method: method } });
        setAccounts(data.accounts || []);
        setAccountId((prev) => (data.accounts || []).some((a) => a.id === prev) ? prev
          : ((data.accounts || []).find((a) => a.padrao) || (data.accounts || [])[0] || {}).id || '');
      } catch (_) { setAccounts([]); }
    })();
  }, [method, open]);

  useEffect(() => {
    if (!accountId || fullDiscount || !line1Gross) { setQuote(null); return; }
    (async () => {
      try {
        const { data } = await api.get('/financial/accounts/fee-quote', {
          params: { account_id: accountId, payment_method: method, installments: Number(form.installments || 1), amount: line1Gross },
        });
        setQuote(data);
      } catch (_) { setQuote(null); }
    })();
  }, [accountId, method, form.installments, line1Gross, fullDiscount]);

  const submit = async () => {
    if (reasonNeeded) { toast.error('Informe o motivo do ajuste manual de desconto.'); return; }
    if (iReasonNeeded) { toast.error('Informe o motivo do juros manual.'); return; }
    if (splitOver) { toast.error('A soma das formas passa do valor devido + encargos.'); return; }
    if (split && extras.some((l) => r2(l.amount) <= 0)) { toast.error('Informe o valor de cada forma de pagamento.'); return; }
    setBusy(true);
    try {
      const body = {
        payment_method: method, account_id: accountId || null, paid_at: paidAt,
        discount_mode: dMode,
        discount_type: dMode === 'manual' ? dType : null,
        discount_value: dMode === 'manual' && dType !== 'final' ? Number(dValue || 0) : null,
        settlement_amount: dMode === 'manual' && dType === 'final'
          ? r2(Math.min(Math.max(Number(dValue || 0), 0), nominalBalance)) : null,
        discount_reason: dMode === 'manual' ? (reason || null) : null,
        apply_late_fee: atrasado && useFee,
        apply_interest: useInterest && (atrasado || iMode === 'manual'),
        interest_mode: iMode,
        interest_type: iMode === 'manual' ? iType : null,
        interest_value: iMode === 'manual' ? Number(iValue || 0) : null,
        interest_reason: iMode === 'manual' ? (iReason || null) : null,
        charges_waiver_reason: atrasado && (!useFee || !useInterest) ? (form.notes || null) : null,
        received_amount: method === 'cash' && form.received_amount ? Number(form.received_amount) : null,
        installments: ['card_credit', 'pix_card'].includes(method) ? Number(form.installments || 1) : 1,
        brand: isCard ? form.brand || null : null,
        last4: isCard ? form.last4 || null : null,
        nsu: isCard ? form.nsu || null : null,
        authorization_code: isCard ? form.authorization_code || null : null,
        acquirer: isCard ? form.acquirer || null : null,
        notes: form.notes || null,
      };
      const { data } = split
        ? await api.post(`/financial/titulos/${titleId}/settle-split`, {
          ...body,
          splits: [
            { payment_method: method, amount: line1Gross, account_id: accountId || null,
              installments: body.installments, received_amount: body.received_amount,
              brand: body.brand, last4: body.last4, nsu: body.nsu, authorization_code: body.authorization_code, acquirer: body.acquirer },
            ...extras.map(lineBody),
          ],
        })
        : titleId
        ? await api.post(`/financial/titulos/${titleId}/settle`, { ...body, amount: gross })
        : await api.post(`/enrollment/processes/${processId}/confirm-payment`,
          { ...body, paid_amount: gross, reason: 'Pagamento registrado no DNA Matrículas' });
      if (split) toast.success(`Recebimento em ${extras.length + 1} formas: bruto ${brl(data.gross_total)} · taxas ${brl(data.fee_total)} · líquido ${brl(data.net_total)}${data.status === 'parcial' ? ` · saldo ${brl(data.balance)}` : ' · título quitado'}.`);
      else if (data.already_paid) { toast.info('Este título já possui pagamento registrado.'); }
      else if (data.full_discount) toast.success('Título quitado com desconto integral — nenhum valor a receber.');
      else if (data.status === 'parcial') {
        toast.success(`Recebimento de ${brl(data.paid_amount)} registrado. Saldo de ${brl(data.balance)}.`);
      } else toast.success(`Recebimento de ${brl(data.paid_amount)} registrado e título baixado.`);
      onSettled?.(data);
      onOpenChange(false);
    } catch (e) { erro(e, 'Erro ao registrar o recebimento.'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto"
        data-testid="payment-settlement-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-violet-600" /> Registrar recebimento
          </DialogTitle>
          <DialogDescription>
            {snap?.description || title || 'Título'}
            {snap?.due_date ? ` · Vencimento ${brDate(snap.due_date)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {!snap ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="pay-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Carregando valores…
          </p>
        ) : (
          <div className="space-y-3">
            {/* 1 · TÍTULO */}
            <Card title="Título">
              <Row label="Valor nominal" value={brl(original)} testid="ps-original" />
              {recebido > 0 && <Row label="Já recebido" value={`- ${brl(recebido)}`} />}
              <Row label="Nominal em aberto" value={brl(nominalBalance)} bold testid="ps-nominal-balance" />
            </Card>

            {/* 2 · DATA DO PAGAMENTO */}
            <Card title="Data do pagamento">
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="ps-date" />
            </Card>

            {/* 3 · DESCONTO / QUITAÇÃO */}
            <Card title="Desconto / quitação" testid="ps-discount-box"
              action={dMode === 'auto' ? (
                <Button size="sm" variant="outline" className="h-7 text-[11.5px]" data-testid="ps-discount-manual"
                  onClick={() => { setDMode('manual'); setDType('final'); setDValue(String(r2(nominalBalance - autoDisc))); }}>
                  <Pencil className="w-3 h-3 mr-1" /> Desconto manual
                </Button>
              ) : (
                <Button size="sm" variant="ghost" className="h-7 text-[11.5px] text-violet-700" data-testid="ps-discount-auto"
                  onClick={() => { setDMode('auto'); setDValue(''); setReason(''); setValor(''); }}>
                  <RotateCcw className="w-3 h-3 mr-1" /> Restaurar automático
                </Button>
              )}>
              {dMode === 'auto' ? (
                <p className="text-sm" data-testid="ps-discount-auto-label">
                  <span className="text-emerald-700 font-semibold">● Automático</span>
                  {' — '}{auto?.label || 'Plano financeiro'}
                  {autoDisc > 0 ? ` · ${auto?.percent}% (${brl(autoDisc)})` : ' · sem desconto'}
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-amber-800">● Manual — quita este título pelo valor definido</p>
                  <div className="flex flex-wrap gap-1">
                    {[['final', 'Valor final'], ['percent', '%'], ['amount', 'R$']].map(([k, l]) => (
                      <button key={k} type="button" data-testid={`ps-discount-type-${k}`}
                        onClick={() => { setDType(k); setDValue(''); setValor(''); }}
                        className={`text-[11.5px] font-semibold rounded-full border px-3 py-1 ${dType === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <Input type="number" step="0.01" min="0" value={dValue} data-testid="ps-discount-value"
                    placeholder={dType === 'final' ? `Valor final (ex.: ${brl(nominalBalance)})` : '0,00'}
                    onChange={(e) => { setDValue(e.target.value); setValor(''); }} />
                  <p className="text-[11.5px] text-slate-600" data-testid="ps-discount-equivalent">
                    Desconto concedido: <b className="text-violet-800">{brl(discount)}</b>
                    {original > 0 ? ` · equivale a ${r2(discount * 100 / original)}%` : ''}
                    {' · '}liquidação por <b>{brl(settlement)}</b>
                  </p>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ps-discount-reason"
                    placeholder="Motivo do desconto (obrigatório)" />
                  {reasonNeeded && <p className="text-[10.5px] text-rose-600">Obrigatório quando o desconto automático é alterado.</p>}
                </div>
              )}
            </Card>

            {/* 4 · ATRASO / ENCARGOS */}
            {atrasado && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="ps-late-box">
                <p className="text-[12.5px] font-bold text-amber-900 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> Pagamento em atraso
                </p>
                <p className="text-[11.5px] text-amber-900" data-testid="ps-late-days">
                  Vencimento {brDate(snap.due_date)} · Pagamento {brDate(paidAt)} · Atraso {diasAtraso} dia(s)
                </p>
                <label className="flex items-center justify-between gap-2 text-[12.5px] font-semibold text-amber-900 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={useFee} data-testid="ps-apply-fee"
                      onChange={(e) => { setUseFee(e.target.checked); setValor(''); }} />
                    Multa {pct(cfg.late_fee_percent)}%
                  </span>
                  <b data-testid="ps-fee-amount">{brl(multaCalc)}</b>
                </label>
                <label className="flex items-center justify-between gap-2 text-[12.5px] font-semibold text-amber-900 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={useInterest} data-testid="ps-apply-interest"
                      onChange={(e) => { setUseInterest(e.target.checked); setValor(''); }} />
                    Juros {iMode === 'manual' ? 'manual' : `${pct(cfg.daily_interest_percent)}% ao dia (${diasJuros} dia(s))`}
                  </span>
                  <b data-testid="ps-interest-amount">{brl(jurosBase)}</b>
                </label>
                {useInterest && (
                  <div className="space-y-1.5 pl-5">
                    <div className="flex gap-1">
                      {[['auto', `Automático ${pct(cfg.daily_interest_percent)}%/dia`], ['manual', 'Manual']].map(([k, lb]) => (
                        <button key={k} type="button"
                          className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                            iMode === k ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-900 border-amber-300'}`}
                          data-testid={`ps-interest-mode-${k}`}
                          onClick={() => { setIMode(k); setValor(''); }}>{lb}</button>
                      ))}
                    </div>
                    {iMode === 'manual' && (
                      <div className="space-y-1.5">
                        <div className="flex gap-1.5">
                          <select className="h-8 rounded-md border px-1 text-[12px]" value={iType}
                            data-testid="ps-interest-type"
                            onChange={(e) => { setIType(e.target.value); setValor(''); }}>
                            <option value="amount">R$</option>
                            <option value="percent">%</option>
                          </select>
                          <Input type="number" step="0.01" min="0" className="h-8 text-[12px]"
                            placeholder={iType === 'percent' ? '% sobre a liquidação' : 'Valor do juros'}
                            value={iValue} data-testid="ps-interest-value"
                            onChange={(e) => { setIValue(e.target.value); setValor(''); }} />
                        </div>
                        <Input className="h-8 text-[12px]" placeholder="Motivo do juros manual (obrigatório)"
                          value={iReason} data-testid="ps-interest-reason"
                          onChange={(e) => setIReason(e.target.value)} />
                        <p className="text-[10.5px] text-amber-800">
                          Substitui o juros só deste recebimento — a configuração global continua
                          {' '}{pct(cfg.daily_interest_percent)}% ao dia.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {(!useFee || !useInterest) && (
                  <p className="text-[11px] text-amber-800" data-testid="ps-waiver-hint">
                    Encargo dispensado — registramos quem dispensou e quando na auditoria do recebimento.
                  </p>
                )}
              </div>
            )}

            {/* 5 · FORMA DE PAGAMENTO */}
            <Card title="Forma de pagamento">
              <div className="flex flex-wrap gap-1">
                {METHODS.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`ps-method-${k}`}
                    className={`text-xs font-semibold rounded-full border px-3 py-1.5 ${method === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {['card_credit', 'pix_card'].includes(method) && (
                <div className="mt-2">
                  <label className="text-xs font-semibold">Parcelas</label>
                  <Input type="number" min="1" max="24" value={form.installments} data-testid="ps-installments"
                    onChange={(e) => set('installments', e.target.value)} />
                </div>
              )}
              {isCard && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input placeholder="Bandeira" value={form.brand} onChange={(e) => set('brand', e.target.value)} data-testid="ps-brand" />
                  <Input placeholder="Últimos 4 dígitos" value={form.last4} onChange={(e) => set('last4', e.target.value)} data-testid="ps-last4" />
                  <Input placeholder="NSU" value={form.nsu} onChange={(e) => set('nsu', e.target.value)} data-testid="ps-nsu" />
                  <Input placeholder="Autorização" value={form.authorization_code} onChange={(e) => set('authorization_code', e.target.value)} data-testid="ps-auth" />
                </div>
              )}
              {method === 'cash' && (
                <div className="mt-2">
                  <label className="text-xs font-semibold">Valor entregue (para troco)</label>
                  <Input type="number" step="0.01" value={form.received_amount} data-testid="ps-received"
                    onChange={(e) => set('received_amount', e.target.value)} />
                  {form.received_amount > gross && (
                    <p className="text-[11px] text-emerald-700 mt-0.5">Troco: {brl(form.received_amount - gross)}</p>
                  )}
                </div>
              )}
            </Card>

            {/* 6 · CONTA */}
            <Card title="Conta de recebimento">
              <select className="w-full h-9 border rounded-md px-2 text-sm" value={accountId}
                onChange={(e) => setAccountId(e.target.value)} data-testid="ps-account">
                <option value="">Selecione a conta</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.nome}{a.provider ? ` · ${a.provider}` : ''}</option>
                ))}
              </select>
              {accounts.length === 0 && (
                <p className="text-[10.5px] text-amber-700 mt-1" data-testid="ps-no-account">
                  Nenhuma conta aceita essa forma — configure em Financeiro → Contas Bancárias.
                </p>
              )}
            </Card>

            {/* 7 · VALOR RECEBIDO + TAXA */}
            <Card title={split ? 'Forma 1 · valor recebido' : 'Valor efetivamente recebido'}>
              <Input type="number" step="0.01" min="0" max={totalReceber} value={valor} data-testid="ps-amount"
                placeholder={brl(totalReceber)} onChange={(e) => setValor(e.target.value)} />
              {parcial && (
                <p className="text-[11.5px] text-amber-700 mt-1" data-testid="ps-partial-hint">
                  Pagamento parcial: o título continua aberto com saldo de {brl(saldoDepois)}.
                </p>
              )}
              <Row label={`Taxa financeira ${quote?.provider || ''}${quote?.fee_percent ? ` (${quote.fee_percent}%)` : ''}`}
                value={`- ${brl(fee)}`} amber />
              <Row label={split ? "Líquido da forma 1" : "Valor líquido na conta"} value={brl(line1Gross - fee)} bold testid="ps-net" />
              <p className="text-[11px] text-slate-500 mt-1">
                A taxa é custo da escola: o título é quitado por {brl(gross)}.
              </p>
              {titleId && !fullDiscount && (
                <div className="mt-2">
                  <SplitPaymentLines extras={extras} setExtras={setExtras} methods={METHODS}
                    line1={{ amount: line1Gross, fee }} devido={totalReceber} />
                </div>
              )}
            </Card>

            {/* 8 · RESUMO FINAL */}
            <div className="rounded-lg border-2 border-slate-900/10 bg-slate-50 p-3 text-sm" data-testid="ps-preview">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Resumo financeiro</p>
              <Row label="Valor original" value={brl(original)} />
              <Row label="Desconto" value={`- ${brl(discount)}`} violet testid="ps-summary-discount" />
              <Row label="Valor após desconto" value={brl(settlement)} testid="ps-summary-settlement" />
              {atrasado && <Row label={`Multa ${useFee ? `${pct(cfg.late_fee_percent)}%` : '(dispensada)'}`} value={`+ ${brl(multa)}`} amber />}
              {(atrasado || (useInterest && iMode === 'manual')) && (
                <Row label={`Juros ${!useInterest ? '(dispensados)'
                  : iMode === 'manual' ? '(manual)' : `${pct(cfg.daily_interest_percent)}%/dia`}`}
                  value={`+ ${brl(juros)}`} amber testid="ps-row-juros" />
              )}
              <Row label="Total a receber" value={brl(totalReceber)} bold testid="ps-summary-total" />
              <Row label="Valor efetivamente recebido" value={brl(gross)} testid="ps-gross" />
              <Row label="Saldo após a baixa" value={brl(saldoDepois)} bold testid="ps-balance" />
            </div>

            <Input placeholder="Observação" value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="ps-notes" />

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 sm:flex-none">Cancelar</Button>
              <Button onClick={submit} disabled={busy || reasonNeeded || iReasonNeeded || splitOver} data-testid="ps-confirm"
                className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {fullDiscount ? 'Confirmar quitação com desconto integral'
                  : parcial ? `Confirmar recebimento parcial de ${brl(gross)}`
                    : `Confirmar recebimento de ${brl(gross)}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Card = ({ title, children, action, testid }) => (
  <div className="rounded-lg border p-3 space-y-1.5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{title}</p>
      {action}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, bold, violet, amber, testid }) => (
  <div className={`flex justify-between text-sm ${violet ? 'text-violet-800' : amber ? 'text-amber-800' : ''} ${bold ? 'border-t mt-1 pt-1 font-semibold' : ''}`}>
    <span>{label}</span><b data-testid={testid}>{value}</b>
  </div>
);
